import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { t } from '../../utils/localization/localization';
import { getDetailedChangeDescriptions } from '../../utils/versionCompare';
import { formatDate } from '../../hooks/useVersionComparison';

/**
 * Dialog component for displaying version change details
 */
const VersionChangeDetailsDialog = ({
  open,
  onClose,
  selectedVersion,
  type,
  onViewVersion
}) => {
  if (!selectedVersion) return null;

  const getItemName = (item, itemType) => {
    try {
      if (!item) {
        console.warn('[VersionChangeDetailsDialog] No item provided for type:', itemType);
        return t('versionHistory.unnamedItem');
      }
      if (type === 'cv') {
        return item.competency || item.name || t('versionHistory.unnamedCompetency');
      } else if (type === 'job') {
        return item.name || t('versionHistory.unnamedSkill');
      } else if (type === 'company') {
        return item.name || item.title || t('versionHistory.unnamedSection');
      }
      return t('versionHistory.unnamedItem');
    } catch (error) {
      console.error('[VersionChangeDetailsDialog] Error getting item name:', error);
      return t('versionHistory.unnamedItem');
    }
  };

  const handleViewVersion = () => {
    onViewVersion(selectedVersion);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('versionHistory.changeDetails')} - {t('versionHistory.version')} {selectedVersion.versionNumber}
        <Typography variant="body2" color="text.secondary">
          {formatDate(selectedVersion.timestamp || selectedVersion.version)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {selectedVersion.comparisonData &&
         selectedVersion.comparisonData.added &&
         selectedVersion.comparisonData.modified &&
         selectedVersion.comparisonData.removed && (
          <Box>
            {/* Added Items */}
            {selectedVersion.comparisonData.added && selectedVersion.comparisonData.added.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="success.main" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AddCircleIcon sx={{ mr: 1 }} />
                  {t('versionHistory.addedItems', { count: selectedVersion.comparisonData.added.length })}
                </Typography>
                <List dense>
                  {selectedVersion.comparisonData.added.map((item, index) => (
                    <ListItem key={`added-${index}`}>
                      <ListItemIcon>
                        <AddCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getItemName(item.item || item, 'added')}
                        secondary={`${t('versionHistory.category')}: ${(item.item || item).category || t('versionHistory.notSpecified')}, ${t('versionHistory.proficiency')}: ${(item.item || item).proficiency || t('versionHistory.notSpecified')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Modified Items */}
            {selectedVersion.comparisonData.modified && selectedVersion.comparisonData.modified.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="primary.main" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CompareArrowsIcon sx={{ mr: 1 }} />
                  {t('versionHistory.modifiedItems', { count: selectedVersion.comparisonData.modified.length })}
                </Typography>
                <List dense>
                  {selectedVersion.comparisonData.modified.map((item, index) => {
                    const itemName = getItemName(item.newItem || item.item || item, 'modified');

                    return (
                      <React.Fragment key={`modified-${index}`}>
                        <ListItem>
                          <ListItemIcon>
                            <CompareArrowsIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={itemName}
                            secondary={
                              <Box>
                                {getDetailedChangeDescriptions(item).map((description, descIndex) => (
                                  <Typography key={descIndex} variant="body2" component="div">
                                    • {description}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < selectedVersion.comparisonData.modified.length - 1 && (
                          <Divider variant="inset" component="li" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Removed Items */}
            {selectedVersion.comparisonData.removed && selectedVersion.comparisonData.removed.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="error.main" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <RemoveCircleIcon sx={{ mr: 1 }} />
                  {t('versionHistory.removedItems', { count: selectedVersion.comparisonData.removed.length })}
                </Typography>
                <List dense>
                  {selectedVersion.comparisonData.removed.map((item, index) => (
                    <ListItem key={`removed-${index}`}>
                      <ListItemIcon>
                        <RemoveCircleIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getItemName(item.item || item, 'removed')}
                        secondary={`${t('versionHistory.category')}: ${(item.item || item).category || t('versionHistory.notSpecified')}, ${t('versionHistory.proficiency')}: ${(item.item || item).proficiency || t('versionHistory.notSpecified')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* No changes message */}
            {(!selectedVersion.comparisonData.added || selectedVersion.comparisonData.added.length === 0) &&
             (!selectedVersion.comparisonData.modified || selectedVersion.comparisonData.modified.length === 0) &&
             (!selectedVersion.comparisonData.removed || selectedVersion.comparisonData.removed.length === 0) && (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('versionHistory.noChangesDetected')}
              </Typography>
            )}
          </Box>
        )}

        {/* Error display */}
        {!selectedVersion.comparisonData && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {t('versionHistory.noComparisonDataAvailable', 'No comparison data available for this version.')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('versionHistory.close')}</Button>
        <Button
          onClick={handleViewVersion}
          variant="contained"
          color="primary"
          startIcon={<VisibilityIcon />}
        >
          {t('versionHistory.viewThisVersion')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionChangeDetailsDialog;
